import React, { createContext } from 'react';

import { StyledEngineProvider } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/react';
import ReactDOM from 'react-dom';
import TagManager from 'react-gtm-module';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { HookReturn } from 'use-places-autocomplete';
import { ToastContainer } from 'react-toastify';
import Hotjar from '@hotjar/browser';

import 'react-toastify/dist/ReactToastify.css';

import { ApplicationEnvironments } from 'enums/common.enums';
import AppRoutes from 'routes';
import store, { intermediaryApi, persistor } from 'store';
import IntermediariesApi from 'api/IntermediariesApi';
import './index.scss';
import NotificationCloseButton from 'components/Buttons/NotificationCloseButton/NotificationCloseButton';
import styles from 'pages/Main/Main.module.scss';
import { ENVIRONMENT, IS_HOTJAR_ENABLED } from 'constants/constants';
import { IGNORED_SENTRY_ERRORS } from 'constants/constants.sentry';

const tagManagerArgs = {
  gtmId: `${process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID}`,
};

TagManager.initialize(tagManagerArgs);

export const IntermediaryApiContext = createContext<IntermediariesApi>(intermediaryApi);
export const GooglePlacesContext = createContext<HookReturn | null>(null);

const initHotJar = () => {
  if (!IS_HOTJAR_ENABLED) {
    return;
  }

  const hotjarVersion = 6;
  const siteId = +process.env.REACT_APP_HOTJAR_ID!;

  Hotjar.init(siteId, hotjarVersion);
};

const initSentry = () => {
  if (![ApplicationEnvironments.Production, ApplicationEnvironments.Testing].includes(ENVIRONMENT)) {
    return;
  }

  Sentry.init({
    environment: ENVIRONMENT,
    dsn: process.env.REACT_APP_SENTRY_DSN,
    tracesSampleRate: 0.1,
    ignoreErrors: IGNORED_SENTRY_ERRORS,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      new BrowserTracing(),
      new Sentry.Replay({
        blockAllMedia: true,
        maskAllText: false,
        maskAllInputs: false,
      }),
    ],
  });
};

initHotJar();
initSentry();

ReactDOM.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <BrowserRouter>
        <StyledEngineProvider injectFirst>
          <AppRoutes />
          <ToastContainer closeButton={NotificationCloseButton} className={styles.toastContainer} limit={3} />
        </StyledEngineProvider>
      </BrowserRouter>
    </PersistGate>
  </Provider>,
  document.getElementById('root'),
);
